import { useHistory } from "react-router-dom";
import { useT } from "@transifex/react";
import BackNavigationButton from "../../basic/backNavigationButton/BackNavigationButton";
import { getClassroomRoute } from "../../helpers/schoolClassHelpers";
import useStudentResult from "../../hooks/useStudentResult";
import ContentWithHeader from "../../wrapper/ContentWithHeader/ContentWithHeader";
import LegalFooter from "../../complex/legalFooter/LegalFooter";
import StudentResultExerciseCard from "./components/StudentResultExerciseCard";
import StudentResultSummary from "./components/StudentResultSummary";
import StudentResultTeacherComment from "./components/StudentResultTeacherComment";
import { TeacherCommentTones } from "../../constants/StudentResultCommentTones";
import { useStudentReviewContext } from "../../context/StudentReviewTestContext";
import DownloadReviewPDF from "../result-pdf/StudentResultPDF";
import { getStudentResultLoadFailure } from "../../helpers/SharedResultAccess";
import { StudentResultLoadFailures } from "../../enums/studentReview";

/**
 * The student-facing result page: summary above, exercise list below, the teacher's whole-test
 * comment last.
 */
const StudentResult = () => {
    const { result, hasLoadingError, classroomId } = useStudentResult();

    const { resultData, studentTest, dataLoaded } = useStudentReviewContext();

    const history = useHistory();
    const t = useT();
    const t_backToClassroom = t("Back to the classroom", { _tags: "StudentResult" });
    const t_notFound = t("This result could not be found.", { _tags: "StudentResult" });
    const t_notLoaded = t("This result could not be loaded. Please try again.", { _tags: "StudentResult" });

    // Absent for legacy share links, which are opened by anonymous link with no classroom behind them.
    const renderBackToClassroom = () => {
        if (!classroomId) return null;
        return (
            <BackNavigationButton
                label={t_backToClassroom}
                onClick={() => history.push(getClassroomRoute(classroomId))}
            />
        );
    };

    // The access guard holds the page on a spinner until the result settles, so the only outcome
    // left here is a failed fetch — and only a failed one, not a dead link, is worth retrying.
    const renderResultBody = () => {
        if (hasLoadingError) {
            const isNotFound = getStudentResultLoadFailure(dataLoaded) === StudentResultLoadFailures.NOT_FOUND;
            return <p data-testid="student-result-not-found">{isNotFound ? t_notFound : t_notLoaded}</p>;
        }
        return (
            <>
                <StudentResultSummary result={result} />
                {/* One box, not a stack of cards: the design separates the rows with a rule only. */}
                <ul
                    className="flex flex-col overflow-hidden rounded-card border border-p6-grey-lightest bg-white divide-y divide-p6-grey-lightest"
                    data-testid="student-result-exercise-list"
                >
                    {result.exercises.map((exercise) => (
                        <li key={exercise.id}>
                            <StudentResultExerciseCard exercise={exercise} />
                        </li>
                    ))}
                </ul>
                {/* Figma draws the whole-test comment in one tone only, whatever the result. */}
                <StudentResultTeacherComment
                    comment={result.overallComment}
                    tone={TeacherCommentTones.RED}
                    isOverall
                />
            </>
        );
    };

    /** Still the pre-redesign PDF: E3 replaces it wholesale (HALLOFUSS-6702), so it gains nothing here. */
    const renderPDFDownloadFooter = () => {
        if (hasLoadingError || dataLoaded !== "LOADED") return null;

        return (
            <div className="flex flex-row bg-white border-t justify-center flex-wrap border-p6-grey-light px-m py-s">
                <div className="flex flex-1 flex-row max-w-[1200px] items-center justify-end">
                    <DownloadReviewPDF
                        resultData={resultData}
                        studentTest={studentTest}
                    />
                </div>
            </div>
        );
    };

    return (
        <ContentWithHeader
            hasGrayBackground
            isFullWidth
            noPadding
            footer={renderPDFDownloadFooter()}
            hasPinnedFooter
        >
            <div className="flex-1 min-h-[0] overflow-y-auto">
                <div
                    className="flex flex-col gap-s w-full max-w-[1248px] min-h-full mx-auto p-m pb-no"
                    data-testid="student-result"
                >
                    {renderBackToClassroom()}
                    {renderResultBody()}
                    <div className="mt-auto">
                        <LegalFooter />
                    </div>
                </div>
            </div>
        </ContentWithHeader>
    );
};

export default StudentResult;
